import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { parseMoneyAmount } from '../../utils/dealMapper'
import { RsIcon, IconLabel } from '../icons'

const DEAL_TYPES = [
  {
    id: 'fundraising',
    icon: 'fund',
    iconClass: 'blue',
    name: 'New Fund Raise',
    desc: 'Raise capital for a new fund. Set target size, minimum commitment, and closing schedule.',
  },
  {
    id: 'fund-exit',
    icon: 'progress',
    iconClass: 'green',
    name: 'Existing Fund Exit',
    desc: 'Sell your LP stake in an existing fund. Provide fund details and your position.',
  },
  {
    id: 'project-share',
    icon: 'portfolio',
    iconClass: 'orange',
    name: 'Single Project Share',
    desc: 'Sell shares of a specific portfolio company or project directly to investors.',
  },
]

const STRATEGIES = [
  'Venture Capital', 'Growth Equity', 'Buyout', 'Private Credit',
  'Real Estate', 'Infrastructure', 'Fund of Funds', 'Other',
]

const GEO_OPTIONS = ['China', 'Southeast Asia', 'US', 'Europe', 'Global']
const STAGE_OPTIONS = ['Seed', 'Series A', 'Series B+', 'Growth', 'Pre-IPO', 'All Stages']
const AUDIENCE_OPTIONS = [
  { id: 'lp', label: 'LPs', icon: 'fund' },
  { id: 'startup', label: 'Startups', icon: 'upgrade' },
  { id: 'gp', label: 'Other GPs', icon: 'bank' },
]

const TYPE_BANNERS = {
  fundraising: { icon: 'fund', title: 'New Fund Raise', text: 'Publish a new fund raise to attract LP commitments on RotationStreet.' },
  'fund-exit': { icon: 'progress', title: 'Existing Fund Exit', text: 'Sell your LP stake or exit an existing fund position on RotationStreet.' },
  'project-share': { icon: 'portfolio', title: 'Single Project Share', text: 'Sell shares of a specific portfolio company or co-investment opportunity.' },
}

const UPLOAD_HINTS = {
  fundraising: 'PPM, Term Sheet, LP Presentation, or any fund documents (PDF, max 20MB each)',
  'fund-exit': 'Capital Call Notice, K-1, NAV Statement, LP Agreement Summary (PDF)',
  'project-share': 'Pitch Deck, Financial Model, Share Purchase Agreement draft (PDF, max 20MB each)',
}

const EMPTY_FORM = {
  title: '',
  gpName: '',
  strategy: '',
  domicile: '',
  targetAmount: '',
  firstClose: '',
  vintageYear: '',
  minCommitment: '',
  managementFee: '',
  carry: '',
  hurdleRate: '',
  keyTerms: '',
  description: '',
  industry: '',
  location: '',
  sectors: ['Technology'],
  geoFocus: ['China'],
  stage: 'Seed',
  trackRecord: '',
  priorPerformance: '',
  portfolioHighlights: '',
  lpCommitment: '',
  stakeToSell: '',
  navMultiple: '',
  askPrice: '',
  capitalCalled: '',
  exitReason: '',
  remainingPeriod: '',
  estimatedMultiple: '',
  portfolioCo1: '',
  portfolioCo2: '',
  portfolioCo3: '',
  fundSize: '',
  entryStage: 'Series B+',
  hqRegion: '',
  investmentYear: '',
  roundSize: '',
  sharesAvailable: '',
  minTicket: '',
  valuation: '',
  entryValuation: '',
  unrealizedMoic: '',
  exitTimeline: '',
  exitPath: 'Secondary Sale',
  thesis: '',
  audience: ['lp', 'startup', 'gp'],
  ndaRequired: 'yes',
  fileNames: [],
}

function Field({ label, required, children, full }) {
  return (
    <div className={`portal-post-deal-field${full ? ' full' : ''}`}>
      {label && (
        <label>
          {label} {required && <span className="req">*</span>}
        </label>
      )}
      {children}
    </div>
  )
}

function Row({ children }) {
  return <div className="portal-post-deal-row">{children}</div>
}

function Section({ num, title, children }) {
  return (
    <div className="portal-post-deal-section">
      <div className="portal-post-deal-section-title">
        <span className="num">{num}</span>
        {title}
      </div>
      {children}
    </div>
  )
}

function TagInput({ tags, onChange, placeholder }) {
  const [draft, setDraft] = useState('')

  const addTag = (value) => {
    const v = value.trim()
    if (!v || tags.includes(v)) return
    onChange([...tags, v])
    setDraft('')
  }

  return (
    <div className="portal-tag-input">
      {tags.map((tag) => (
        <span key={tag} className="portal-tag-bubble">
          {tag}
          <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))} aria-label={`Remove ${tag}`}>
            ×
          </button>
        </span>
      ))}
      <input
        className="portal-tag-input-field"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            addTag(draft)
          }
        }}
        onBlur={() => addTag(draft)}
        placeholder={placeholder}
      />
    </div>
  )
}

function ChipGroup({ options, value, onChange, multiple }) {
  const toggle = (id) => {
    if (multiple) {
      onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
    } else {
      onChange(id)
    }
  }

  return (
    <div className="portal-chip-group">
      {options.map((opt) => {
        const id = typeof opt === 'string' ? opt : opt.id
        const label = typeof opt === 'string' ? opt : opt.label
        const selected = multiple ? value.includes(id) : value === id
        return (
          <label key={id} className={`portal-chip${selected ? ' selected' : ''}`}>
            <input
              type={multiple ? 'checkbox' : 'radio'}
              checked={selected}
              onChange={() => toggle(id)}
            />
            {label}
          </label>
        )
      })}
    </div>
  )
}

function UploadZone({ hint, files, onFiles }) {
  return (
    <label className="portal-upload-zone">
      <div className="portal-upload-icon"><RsIcon name="folder" size="lg" /></div>
      <div className="portal-upload-text">
        Drop files here or <strong>click to upload</strong>
      </div>
      <div className="portal-upload-sub">{hint}</div>
      {files.length > 0 && (
        <div className="portal-upload-list">
          {files.map((f) => (
            <span key={f} className="portal-upload-file">{f}</span>
          ))}
        </div>
      )}
      <input
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.ppt,.pptx"
        className="sr-only"
        onChange={(e) => {
          const names = Array.from(e.target.files || []).map((f) => f.name)
          if (names.length) onFiles([...files, ...names])
          e.target.value = ''
        }}
      />
    </label>
  )
}

function DocumentsSection({ form, setForm, dealType }) {
  return (
    <Section num={dealType === 'fundraising' ? 6 : 5} title="Documents & Visibility">
      <UploadZone
        hint={UPLOAD_HINTS[dealType]}
        files={form.fileNames}
        onFiles={(fileNames) => setForm((f) => ({ ...f, fileNames }))}
      />
      <Row>
        <Field label="Who can see this deal?">
          <ChipGroup
            multiple
            options={AUDIENCE_OPTIONS}
            value={form.audience}
            onChange={(audience) => setForm((f) => ({ ...f, audience }))}
          />
        </Field>
      </Row>
      <Row>
        <Field label="Requires NDA before sharing details?">
          <ChipGroup
            options={[
              { id: 'no', label: 'No NDA needed' },
              { id: 'yes', label: 'NDA required to view full details' },
            ]}
            value={form.ndaRequired}
            onChange={(ndaRequired) => setForm((f) => ({ ...f, ndaRequired }))}
          />
        </Field>
      </Row>
    </Section>
  )
}

function FundraisingFields({ form, setForm, setField }) {
  return (
    <>
      <Section num={2} title="Fund Basics">
        <Row>
          <Field label="Fund Name" required>
            <input type="text" value={form.title} onChange={setField('title')} placeholder="e.g. Sequoia China Growth Fund VI" required />
          </Field>
          <Field label="Fund Manager / GP" required>
            <input type="text" value={form.gpName} onChange={setField('gpName')} placeholder="e.g. Sequoia Capital China" required />
          </Field>
        </Row>
        <Row>
          <Field label="Fund Strategy">
            <select value={form.strategy} onChange={setField('strategy')}>
              <option value="">Select strategy...</option>
              {STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Fund Domicile">
            <select value={form.domicile} onChange={setField('domicile')}>
              <option value="">Select jurisdiction...</option>
              <option value="Cayman Islands">Cayman Islands</option>
              <option value="Singapore">Singapore</option>
              <option value="Hong Kong">Hong Kong</option>
              <option value="Delaware (US)">Delaware (US)</option>
              <option value="Luxembourg">Luxembourg</option>
              <option value="Other">Other</option>
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Target Size" required>
            <input type="text" value={form.targetAmount} onChange={setField('targetAmount')} placeholder="e.g. $500M" required />
          </Field>
          <Field label="First Close Target">
            <input type="text" value={form.firstClose} onChange={setField('firstClose')} placeholder="e.g. $100M" />
          </Field>
        </Row>
        <Row>
          <Field label="Fund Vintage Year">
            <select value={form.vintageYear} onChange={setField('vintageYear')}>
              <option value="">Select year...</option>
              {['2025', '2026', '2027', '2028'].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="Investment Period">
            <select value={form.remainingPeriod} onChange={setField('remainingPeriod')}>
              <option value="">Select...</option>
              <option value="3 years">3 years</option>
              <option value="4 years">4 years</option>
              <option value="5 years">5 years</option>
              <option value="6 years">6 years</option>
            </select>
          </Field>
        </Row>
        <Field label="Fund Description" required full>
          <textarea
            value={form.description}
            onChange={setField('description')}
            placeholder="Describe the fund's investment thesis, strategy, and key differentiators..."
            required
          />
        </Field>
      </Section>

      <Section num={3} title="Minimum Commitment & Terms">
        <Row>
          <Field label="Minimum LP Commitment" required>
            <input type="text" value={form.minCommitment} onChange={setField('minCommitment')} placeholder="e.g. $5M" required />
          </Field>
          <Field label="Management Fee">
            <input type="text" value={form.managementFee} onChange={setField('managementFee')} placeholder="e.g. 2% on committed capital" />
          </Field>
        </Row>
        <Row>
          <Field label="Carry (Performance Fee)">
            <input type="text" value={form.carry} onChange={setField('carry')} placeholder="e.g. 20%" />
          </Field>
          <Field label="Hurdle Rate">
            <input type="text" value={form.hurdleRate} onChange={setField('hurdleRate')} placeholder="e.g. 8%" />
          </Field>
        </Row>
        <Field label="Key Terms Summary" full>
          <textarea
            value={form.keyTerms}
            onChange={setField('keyTerms')}
            placeholder="Summarize other key terms: side pocket rights, GP commitment, MFN clause, etc..."
          />
        </Field>
      </Section>

      <Section num={4} title="Investment Focus">
        <Field label="Primary Sector(s)">
          <TagInput
            tags={form.sectors}
            onChange={(sectors) => setForm((f) => ({ ...f, sectors }))}
            placeholder="+ Add sector"
          />
        </Field>
        <Field label="Geographic Focus">
          <ChipGroup
            multiple
            options={GEO_OPTIONS}
            value={form.geoFocus}
            onChange={(geoFocus) => setForm((f) => ({ ...f, geoFocus }))}
          />
        </Field>
        <Field label="Stage Preference">
          <ChipGroup
            options={STAGE_OPTIONS}
            value={form.stage}
            onChange={(stage) => setForm((f) => ({ ...f, stage }))}
          />
        </Field>
      </Section>

      <Section num={5} title="Track Record">
        <Row>
          <Field label="GP / Manager Track Record">
            <input type="text" value={form.trackRecord} onChange={setField('trackRecord')} placeholder="e.g. 12 years, $8B AUM" />
          </Field>
          <Field label="Prior Fund(s) Performance">
            <input type="text" value={form.priorPerformance} onChange={setField('priorPerformance')} placeholder="e.g. Fund I: 3.2× MOIC" />
          </Field>
        </Row>
        <Field label="Key Portfolio Exits / Highlights" full>
          <textarea
            value={form.portfolioHighlights}
            onChange={setField('portfolioHighlights')}
            placeholder="Notable exits, IPOs, or significant value creation events..."
          />
        </Field>
      </Section>
    </>
  )
}

function FundExitFields({ form, setForm, setField }) {
  return (
    <>
      <Section num={2} title="Fund Information">
        <Row>
          <Field label="Fund Name" required>
            <input type="text" value={form.title} onChange={setField('title')} placeholder="e.g. Sequoia China Fund IV" required />
          </Field>
          <Field label="Fund Manager / GP" required>
            <input type="text" value={form.gpName} onChange={setField('gpName')} placeholder="e.g. Sequoia Capital China" required />
          </Field>
        </Row>
        <Row>
          <Field label="Vintage Year">
            <select value={form.vintageYear} onChange={setField('vintageYear')}>
              <option value="">Select year...</option>
              {['2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024'].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>
          <Field label="Fund Domicile">
            <select value={form.domicile} onChange={setField('domicile')}>
              <option value="">Select jurisdiction...</option>
              <option value="Cayman Islands">Cayman Islands</option>
              <option value="Singapore">Singapore</option>
              <option value="Hong Kong">Hong Kong</option>
              <option value="Delaware (US)">Delaware (US)</option>
              <option value="Luxembourg">Luxembourg</option>
              <option value="Other">Other</option>
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Your LP Commitment" required>
            <input type="text" value={form.lpCommitment} onChange={setField('lpCommitment')} placeholder="e.g. $20M" required />
          </Field>
          <Field label="Your Stake to Sell" required>
            <input type="text" value={form.stakeToSell} onChange={setField('stakeToSell')} placeholder="e.g. 100% or $20M" required />
          </Field>
        </Row>
        <Row>
          <Field label="Current NAV Multiple">
            <input type="text" value={form.navMultiple} onChange={setField('navMultiple')} placeholder="e.g. 1.8×" />
          </Field>
          <Field label="Ask Price" required>
            <input type="text" value={form.askPrice} onChange={setField('askPrice')} placeholder="e.g. $36M or 1.6× NAV" required />
          </Field>
        </Row>
      </Section>

      <Section num={3} title="Fund Details">
        <Row>
          <Field label="Fund Strategy">
            <select value={form.strategy} onChange={setField('strategy')}>
              <option value="">Select strategy...</option>
              {STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Capital Called to Date">
            <input type="text" value={form.capitalCalled} onChange={setField('capitalCalled')} placeholder="e.g. 65%" />
          </Field>
        </Row>
        <Field label="Why are you selling?" full>
          <textarea
            value={form.exitReason}
            onChange={setField('exitReason')}
            placeholder="Brief reason for exit (e.g. portfolio rebalancing, liquidity needs)..."
          />
        </Field>
        <Row>
          <Field label="Remaining Investment Period">
            <input type="text" value={form.remainingPeriod} onChange={setField('remainingPeriod')} placeholder="e.g. ~2 years" />
          </Field>
          <Field label="Estimated Final Multiple">
            <input type="text" value={form.estimatedMultiple} onChange={setField('estimatedMultiple')} placeholder="e.g. 2.5× projected" />
          </Field>
        </Row>
      </Section>

      <Section num={4} title="Key Portfolio Companies (Top 3)">
        <Row>
          <Field label="Portfolio Company #1">
            <input type="text" value={form.portfolioCo1} onChange={setField('portfolioCo1')} placeholder="e.g. ByteDance (estimated 20% of NAV)" />
          </Field>
          <Field label="Portfolio Company #2">
            <input type="text" value={form.portfolioCo2} onChange={setField('portfolioCo2')} placeholder="e.g. JD Logistics" />
          </Field>
        </Row>
        <Row>
          <Field label="Portfolio Company #3">
            <input type="text" value={form.portfolioCo3} onChange={setField('portfolioCo3')} placeholder="e.g. Shein" />
          </Field>
          <Field label="Fund Size (Total Committed Capital)">
            <input type="text" value={form.fundSize} onChange={setField('fundSize')} placeholder="e.g. $1.2B" />
          </Field>
        </Row>
      </Section>
    </>
  )
}

function ProjectShareFields({ form, setForm, setField }) {
  return (
    <>
      <Section num={2} title="Project / Company Information">
        <Row>
          <Field label="Company / Project Name" required>
            <input type="text" value={form.title} onChange={setField('title')} placeholder="e.g. ByteDance Ltd." required />
          </Field>
          <Field label="GP / Lead Investor" required>
            <input type="text" value={form.gpName} onChange={setField('gpName')} placeholder="e.g. Sequoia Capital China" required />
          </Field>
        </Row>
        <Row>
          <Field label="Industry / Sector" required>
            <select value={form.industry} onChange={setField('industry')} required>
              <option value="">Select sector...</option>
              {['Technology', 'Healthcare', 'Consumer', 'Financial Services', 'Industrial', 'Energy', 'Real Estate', 'Education', 'Logistics', 'Other'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Stage at Entry">
            <ChipGroup
              options={STAGE_OPTIONS.filter((s) => s !== 'All Stages')}
              value={form.entryStage}
              onChange={(entryStage) => setForm((f) => ({ ...f, entryStage }))}
            />
          </Field>
        </Row>
        <Row>
          <Field label="HQ Country / Region">
            <select value={form.hqRegion} onChange={setField('hqRegion')}>
              <option value="">Select region...</option>
              {['China', 'Singapore', 'Hong Kong', 'USA', 'UK', 'India', 'Southeast Asia', 'Europe', 'Other'].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
          <Field label="Year of Investment">
            <select value={form.investmentYear} onChange={setField('investmentYear')}>
              <option value="">Select year...</option>
              {['2020', '2021', '2022', '2023', '2024', '2025'].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
        </Row>
      </Section>

      <Section num={3} title="Deal Terms">
        <Row>
          <Field label="Total Round Size">
            <input type="text" value={form.roundSize} onChange={setField('roundSize')} placeholder="e.g. $200M" />
          </Field>
          <Field label="Your Shares Available" required>
            <input type="text" value={form.sharesAvailable} onChange={setField('sharesAvailable')} placeholder="e.g. $15M or 7.5% equity" required />
          </Field>
        </Row>
        <Row>
          <Field label="Minimum Co-Investment Ticket">
            <input type="text" value={form.minTicket} onChange={setField('minTicket')} placeholder="e.g. $2M" />
          </Field>
          <Field label="Valuation at This Round" required>
            <input type="text" value={form.valuation} onChange={setField('valuation')} placeholder="e.g. $3B post-money" required />
          </Field>
        </Row>
        <Row>
          <Field label="Entry Valuation (Your Cost)">
            <input type="text" value={form.entryValuation} onChange={setField('entryValuation')} placeholder="e.g. $1.2B (2022 round)" />
          </Field>
          <Field label="Unrealized MOIC">
            <input type="text" value={form.unrealizedMoic} onChange={setField('unrealizedMoic')} placeholder="e.g. 2.5×" />
          </Field>
        </Row>
      </Section>

      <Section num={4} title="Exit & Returns">
        <Row>
          <Field label="Expected Exit Timeline">
            <input type="text" value={form.exitTimeline} onChange={setField('exitTimeline')} placeholder="e.g. 18–24 months" />
          </Field>
          <Field label="Expected Exit Path">
            <ChipGroup
              options={['IPO', 'M&A', 'Secondary Sale', 'Open to All']}
              value={form.exitPath}
              onChange={(exitPath) => setForm((f) => ({ ...f, exitPath }))}
            />
          </Field>
        </Row>
        <Field label="Investment Thesis / Why This Company?" required full>
          <textarea
            value={form.thesis}
            onChange={setField('thesis')}
            placeholder="Describe the investment thesis and why you believe in this company's growth potential..."
            required
          />
        </Field>
      </Section>
    </>
  )
}

function buildDescription(dealType, form) {
  const typeName = DEAL_TYPES.find((t) => t.id === dealType)?.name
  const lines = [`Deal type: ${typeName}`, form.gpName ? `GP / Manager: ${form.gpName}` : '']

  if (dealType === 'fundraising') {
    lines.push(
      form.strategy ? `Strategy: ${form.strategy}` : '',
      form.domicile ? `Domicile: ${form.domicile}` : '',
      form.firstClose ? `First close: ${form.firstClose}` : '',
      form.vintageYear ? `Vintage: ${form.vintageYear}` : '',
      form.minCommitment ? `Min commitment: ${form.minCommitment}` : '',
      form.managementFee ? `Management fee: ${form.managementFee}` : '',
      form.carry ? `Carry: ${form.carry}` : '',
      form.hurdleRate ? `Hurdle: ${form.hurdleRate}` : '',
      form.keyTerms ? `Key terms: ${form.keyTerms}` : '',
      form.sectors.length ? `Sectors: ${form.sectors.join(', ')}` : '',
      form.geoFocus.length ? `Geography: ${form.geoFocus.join(', ')}` : '',
      form.stage ? `Stage: ${form.stage}` : '',
      form.trackRecord ? `Track record: ${form.trackRecord}` : '',
      form.priorPerformance ? `Prior funds: ${form.priorPerformance}` : '',
      form.portfolioHighlights ? `Highlights: ${form.portfolioHighlights}` : '',
      form.description,
    )
  } else if (dealType === 'fund-exit') {
    lines.push(
      form.vintageYear ? `Vintage: ${form.vintageYear}` : '',
      form.lpCommitment ? `LP commitment: ${form.lpCommitment}` : '',
      form.stakeToSell ? `Stake to sell: ${form.stakeToSell}` : '',
      form.navMultiple ? `NAV multiple: ${form.navMultiple}` : '',
      form.askPrice ? `Ask price: ${form.askPrice}` : '',
      form.strategy ? `Strategy: ${form.strategy}` : '',
      form.capitalCalled ? `Capital called: ${form.capitalCalled}` : '',
      form.exitReason ? `Exit reason: ${form.exitReason}` : '',
      form.remainingPeriod ? `Remaining period: ${form.remainingPeriod}` : '',
      form.estimatedMultiple ? `Est. multiple: ${form.estimatedMultiple}` : '',
      form.portfolioCo1 ? `Top holding 1: ${form.portfolioCo1}` : '',
      form.portfolioCo2 ? `Top holding 2: ${form.portfolioCo2}` : '',
      form.portfolioCo3 ? `Top holding 3: ${form.portfolioCo3}` : '',
      form.fundSize ? `Fund size: ${form.fundSize}` : '',
    )
  } else {
    lines.push(
      form.industry ? `Sector: ${form.industry}` : '',
      form.entryStage ? `Entry stage: ${form.entryStage}` : '',
      form.hqRegion ? `HQ: ${form.hqRegion}` : '',
      form.investmentYear ? `Investment year: ${form.investmentYear}` : '',
      form.roundSize ? `Round size: ${form.roundSize}` : '',
      form.sharesAvailable ? `Shares available: ${form.sharesAvailable}` : '',
      form.minTicket ? `Min ticket: ${form.minTicket}` : '',
      form.valuation ? `Valuation: ${form.valuation}` : '',
      form.entryValuation ? `Entry valuation: ${form.entryValuation}` : '',
      form.unrealizedMoic ? `MOIC: ${form.unrealizedMoic}` : '',
      form.exitTimeline ? `Exit timeline: ${form.exitTimeline}` : '',
      form.exitPath ? `Exit path: ${form.exitPath}` : '',
      form.thesis,
    )
  }

  lines.push(
    form.audience.length ? `Visible to: ${form.audience.join(', ')}` : '',
    form.ndaRequired === 'yes' ? 'NDA required' : 'No NDA required',
    form.fileNames.length ? `Attachments (pending upload): ${form.fileNames.join(', ')}` : '',
  )

  return lines.filter(Boolean).join('\n')
}

function resolveTargetAmount(dealType, form) {
  if (dealType === 'fund-exit') return form.askPrice || form.stakeToSell || form.lpCommitment
  if (dealType === 'project-share') return form.valuation || form.sharesAvailable || form.roundSize
  return form.targetAmount
}

function resolveLocation(dealType, form) {
  if (dealType === 'project-share') return form.hqRegion || form.location
  if (dealType === 'fund-exit') return form.domicile || form.location
  return form.geoFocus.join(', ') || form.domicile || form.location
}

export default function PortalPostDealForm({ onSubmit, onCancel, loading }) {
  const [dealType, setDealType] = useState('fundraising')
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [showPreview, setShowPreview] = useState(false)

  const banner = TYPE_BANNERS[dealType]
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleTypeChange = (typeId) => {
    setDealType(typeId)
    setForm({ ...EMPTY_FORM })
    setShowPreview(false)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const targetAmount = resolveTargetAmount(dealType, form)
    if (!form.title.trim()) {
      toast.error('Please enter a deal title')
      return
    }
    if (!form.gpName.trim()) {
      toast.error('Please enter GP / manager name')
      return
    }
    if (targetAmount && parseMoneyAmount(targetAmount) == null) {
      toast.error('Target amount must be a valid number (e.g. $500M or 1000000)')
      return
    }
    if (dealType === 'fundraising' && !form.description.trim()) {
      toast.error('Please enter a fund description')
      return
    }
    onSubmit({
      title: form.title.trim(),
      description: buildDescription(dealType, form),
      targetAmount,
      industry: form.strategy || form.industry || form.sectors?.[0] || '',
      location: resolveLocation(dealType, form),
    })
  }

  const handleDraft = () => {
    try {
      localStorage.setItem(`rs-deal-draft-${dealType}`, JSON.stringify(form))
      toast.success('Draft saved locally')
    } catch {
      toast.error('Could not save draft')
    }
  }

  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(`rs-deal-draft-${dealType}`)
      if (raw) {
        setForm({ ...EMPTY_FORM, ...JSON.parse(raw) })
        toast.success('Draft restored')
      } else {
        toast('No draft found for this deal type')
      }
    } catch {
      toast.error('Could not load draft')
    }
  }

  return (
    <div className="portal-post-deal">
      <div className="portal-post-deal-card">
        <div className="portal-post-deal-section">
          <div className="portal-post-deal-section-title">
            <span className="num">1</span>
            What are you raising or selling?
          </div>
          <div className="portal-deal-type-grid">
            {DEAL_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                className={`portal-deal-type-card${dealType === type.id ? ' active' : ''}`}
                onClick={() => handleTypeChange(type.id)}
              >
                <div className={`portal-deal-type-icon ${type.iconClass}`}><RsIcon name={type.icon} size="lg" /></div>
                <div className="portal-deal-type-name">{type.name}</div>
                <div className="portal-deal-type-desc">{type.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="portal-post-deal-preview">
        <span className="icon"><RsIcon name={banner.icon} size="md" /></span>
        <p>
          <strong>{banner.title}</strong> — {banner.text}
        </p>
      </div>

      {showPreview && (
        <div className="portal-post-deal-preview portal-post-deal-preview-detail">
          <strong>Preview summary</strong>
          <pre>{buildDescription(dealType, form)}</pre>
        </div>
      )}

      <form onSubmit={handleSubmit} className="portal-post-deal-card">
        {dealType === 'fundraising' && <FundraisingFields form={form} setForm={setForm} setField={setField} />}
        {dealType === 'fund-exit' && <FundExitFields form={form} setForm={setForm} setField={setField} />}
        {dealType === 'project-share' && <ProjectShareFields form={form} setForm={setForm} setField={setField} />}

        <DocumentsSection form={form} setForm={setForm} dealType={dealType} />

        <div className="portal-post-deal-action-bar">
          <div className="portal-post-deal-action-left">
            <button type="button" className="portal-save-draft-btn" onClick={handleDraft}>
              Save as Draft
            </button>
            <button type="button" className="portal-save-draft-btn" onClick={loadDraft}>
              Restore Draft
            </button>
          </div>
          <div className="portal-post-deal-action-right">
            <button type="button" className="portal-btn-view" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="portal-btn-view" onClick={() => setShowPreview((p) => !p)}>
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button type="submit" className="portal-btn-primary" disabled={loading}>
              {loading ? 'Submitting…' : 'Submit for Review'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
